import { normalize } from 'normalizr';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import * as types from './constants';
import Http from '../../http';
import * as schema from './schema';
import { addShopImage, updateShop } from '../../main/shops/actions';
import { flags as ldFlagNames } from '../../../modules/infra/feature-flags/flags';

const PER_PAGE = 500;

export const fetchShopsRequest = () => ({
  type: types.FETCH_SHOPS_REQUEST,
});

export const fetchShopsSuccess = shops => ({
  type: types.FETCH_SHOPS_SUCCESS,
  payload: shops,
});

export const fetchShopsDisabledSuccess = shops => ({
  type: types.FETCH_SHOPS_DISABLED_SUCCESS,
  payload: shops,
});

const fetchAdminShopsByIdSuccess = shops => ({
  type: types.FETCH_ADMIN_SHOPS_BY_ID,
  payload: shops,
});

const fetchShopsApi = (skip = 0) => Http
  .get('/shop', {
    limit: PER_PAGE,
    sortBy: 'name:asc',
    skip,
  }).then((shops) => {
    if (shops.length === PER_PAGE) {
      return fetchShopsApi(skip + PER_PAGE).then(data1 => [...shops, ...data1]);
    }
    return shops;
  });

export const fetchShops = (skip = 0) => (dispatch) => {
  dispatch(fetchShopsRequest());
  return fetchShopsApi(skip)
    .then(shops => dispatch(fetchShopsSuccess(shops)));
};

export const fetchAdminShopsByIds = ids => dispatch =>
  Http.get(`/shop?id__in=${ids.join(',')}`).then(shops => dispatch(fetchAdminShopsByIdSuccess(shops)));

export const updateAdminShop = (shopId, data, currentShopData) => async (dispatch) => {
  const shop = await dispatch(updateShop(shopId, data, currentShopData));
  await dispatch(fetchAdminShopsByIds([shopId]));
  return shop;
};

export const saveShopRequest = () => ({
  type: types.SAVE_SHOP_REQUEST,
});

export const saveShopSuccess = shop => ({
  type: types.SAVE_SHOP_SUCCESS,
  response: normalize(shop, schema.shop),
});

export const fetchShopSuccess = shop => ({
  type: types.FETCH_SHOP_SUCCESS,
  response: normalize(shop, schema.shop),
});

export const saveShop = (data) => {
  return (dispatch) => {
    let avatar = null;

    if (data.avatar instanceof File) {
      avatar = data.avatar;
      delete data.avatar;
    }
    // clean all empty keys
    data.address = omitBy(data.address, isEmpty);
    let id;
    dispatch(saveShopRequest());
    return Http.post('/shop', data)
      .then((shop) => {
        id = shop.id;
        // save shop images
        if (!avatar) {
          return Promise.resolve();
        }

        return dispatch(addShopImage(id, avatar));
      })
      .then(() => Http.get(`/shop/${id}`, { include: 'address,barbers,photos,schedules,logo,users,brand' }))
      .then(shop => dispatch(saveShopSuccess(shop)))
      .then(() => dispatch(fetchAdminShopsByIds([id])));
  };
};

export const fetchShop = (id) => {
  return async (dispatch) => {
    const shop = await Http.get(`/shop/${id}`, {
      include: 'address,barbers,photos,schedules,logo,users.brands,brand,days_off',
    });

    dispatch(fetchShopSuccess(shop));
    return shop;
  };
};

export const customersImport = (shopId, barberId, data) => (dispatch) => {
  let url = `/shop/${shopId}/import-customers`;
  if (barberId) {
    url += `?barberId=${barberId}`;
  }

  return Http.postRaw(url, data);
};

export const deleteImportedCustomers = shopId => dispatch =>
  Http.delete(`/shop/${shopId}/delete-customers`);

export const deleteAllCustomers = shopId => dispatch =>
  Http.post('/jobs/wipe-customers', { ownerId: shopId, ownerType: 'shop' });

export const appointmentsImport = (shopId, file) => dispatch =>
  Http.postRaw(`/shop/${shopId}/import-appointments`, { file });

export const productsImport = (shopId, file) => () =>
  Http.postRaw(`/shop/${shopId}/import-products`, { file });

export const servicesImport = (shopId, file) => () =>
  Http.postRaw(`/shop/${shopId}/import-services`, { file });

export const giftCardsImport = (shopId, file) => () =>
  Http.postRaw(`/shop/${shopId}/import-gift-cards`, { file });

export const reviewsImport = (shopId, data) => () =>
  Http.post('/reviews/scrape', {
    urls: data.urls,
    entityId: shopId,
    entityType: 'shop',
  });

export const deleteImportedAppointments = shopId => dispatch =>
  Http.delete(`/shop/${shopId}/delete-appointments`);

const fetchLoanAction = payload => ({
  type: types.FETCH_SHOP_LOAN,
  payload,
});

export const fetchShopLoan = shopId => async (dispatch) => {
  const response = await Http.get(`/shop/${shopId}/loan`);
  return dispatch(fetchLoanAction(response));
};

const createLoanAction = payload => ({
  type: types.CREATE_SHOP_LOAN,
  payload,
});

export const createShopLoan = (shopId, data) => async (dispatch) => {
  const response = await Http.post(`/shop/${shopId}/loan`, data);
  return dispatch(createLoanAction(response));
};

const updateLoanAction = payload => ({
  type: types.UPDATE_SHOP_LOAN,
  payload,
});

export const updateShopLoan = (shopId, data) => async (dispatch) => {
  const response = await Http.put(`/shop/${shopId}/loan`, data);
  return dispatch(updateLoanAction(response));
};

export const fetchAllCoupons = () => dispatch =>
  Http.get('/subscription/coupons').then(coupons => dispatch({
    type: types.FETCH_COUPONS,
    payload: coupons,
  }));

export const fetchHourlyWageRules = shopId => dispatch =>
  Http.get(`/shop/${shopId}/hourly-wage-rules`).then(hourlyWageRules =>
    dispatch({
      type: types.FETCH_HOURLY_WAGE_RULES,
      payload: hourlyWageRules,
    })
  );

export const saveHourlyWageRules = (shopId, data) => dispatch =>
  Http.post(`/shop/${shopId}/hourly-wage-rules`, data).then(hourlyWageRules =>
    dispatch({
      type: types.FETCH_HOURLY_WAGE_RULES,
      payload: hourlyWageRules,
    })
  );

export const deleteImportedProducts = shopId => dispatch =>
  Http.delete(`/shop/${shopId}/delete-products`);

export const deleteImportedServices = shopId => dispatch =>
  Http.delete(`/shop/${shopId}/delete-services`);

export const deleteImportedGiftCards = shopId => dispatch =>
  Http.delete(`/shop/${shopId}/delete-gift-cards`);

export const saveShopConnectedAccount = (shopId, data) => dispatch =>
  Http.post(`/shop/${shopId}/connected-account`, data);

export const saveBarberConnectedAccount = (barberId, data) => dispatch =>
  Http.post(`/barber/${barberId}/connected-account`, data);

export const fetchShopLdFlags = (shopId) => async () => {
  const flagsToFetch = Object.keys(ldFlagNames).map((flagName) => flagName.replace(/([A-Z])/g, '-$1').toLowerCase());

  const flags = await Http.get(`/shop/${shopId}/flags`, {
    names: flagsToFetch.join(','),
  });

  return flags;
};
